import { BusinessObjectBase } from './base';
import { packageVersionSchema } from '@schemas/business-objects/package-version';
import type { NpmPackage } from './npm-package';
import type { User } from './user';
import type { Id, VersionSource } from '../base-types';

export interface PackageVersionData {
  readonly id: Id;
  readonly version?: string;
  readonly manifestJson?: string;
  readonly readme?: string;
  readonly shasum?: string;
  readonly integrity?: string;
  readonly publishedAt?: Date;
  readonly publishedFromRepository?: string;
  readonly publishedFromWorkflow?: string;
  readonly publishedFromRunId?: string;
  readonly actor?: string;
  readonly deprecatedMessage?: string;
  readonly source?: VersionSource;
  readonly fileIndexJson?: string;
  readonly unpackedAt?: Date;
  readonly npmPackageId: Id;
  readonly npmPackage?: NpmPackage;
  readonly publishedById: Id;
  readonly publishedBy?: User;
}

export interface PackageVersion extends Readonly<PackageVersionData> {}
export class PackageVersion extends BusinessObjectBase {
  static readonly entityName = 'PackageVersion' as const;
  static readonly plural = 'package-versions';
  static schema = packageVersionSchema;

  // --- Static CRUD methods ---

  static async find(_filter?: any): Promise<PackageVersion[]> {
    throw new Error('find is not available on PackageVersion');
  }

  static async findOne(_filter?: any): Promise<PackageVersion | null> {
    throw new Error('findOne is not available on PackageVersion');
  }

  static async findById(_id: Id, _filter?: any): Promise<PackageVersion> {
    throw new Error('findById is not available on PackageVersion');
  }

  static async create(_data: any): Promise<PackageVersion> {
    throw new Error('create is not available on PackageVersion');
  }

  static async createMany(_data: any[]): Promise<PackageVersion[]> {
    throw new Error('createMany is not available on PackageVersion');
  }

  static async createWithChildren(_data: any): Promise<PackageVersion> {
    throw new Error('createWithChildren is not available on PackageVersion');
  }

  static async createManyWithChildren(_data: any[]): Promise<PackageVersion[]> {
    throw new Error(
      'createManyWithChildren is not available on PackageVersion',
    );
  }

  static async updateById(_id: Id, _updates: any): Promise<PackageVersion> {
    throw new Error('updateById is not available on PackageVersion');
  }

  static async deleteById(_id: Id): Promise<boolean> {
    throw new Error('deleteById is not available on PackageVersion');
  }

  // --- Behaviors ---

  async fileContent(path: string): Promise<void> {
    const url = `${BusinessObjectBase.baseUrl}/${PackageVersion.plural}/${(this as any).id}/file-content`;
    const queryParams = new URLSearchParams();
    if (path !== undefined && path !== null)
      queryParams.set('path', String(path));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return BusinessObjectBase.get<void>(fullUrl);
  }
  async files(): Promise<{
    unpackedAt: Date;
    readme?: string;
    files: { path: string; size: number; text: boolean }[];
  }> {
    const url = `${BusinessObjectBase.baseUrl}/${PackageVersion.plural}/${(this as any).id}/files`;
    return BusinessObjectBase.get<{
      unpackedAt: Date;
      readme?: string;
      files: { path: string; size: number; text: boolean }[];
    }>(url);
  }
  async fileData(path: string): Promise<{
    path: string;
    contentType: string;
    text: boolean;
    base64Content: string;
  }> {
    const url = `${BusinessObjectBase.baseUrl}/${PackageVersion.plural}/${(this as any).id}/file-data`;
    const queryParams = new URLSearchParams();
    if (path !== undefined && path !== null)
      queryParams.set('path', String(path));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return BusinessObjectBase.get<{
      path: string;
      contentType: string;
      text: boolean;
      base64Content: string;
    }>(fullUrl);
  }
}
